import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { WorkflowService } from '@/pages/service/core/workflow.service';
import { AppDatePipe } from '@/pages/service/core/app-date.pipe';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SelectModule } from 'primeng/select';
import { forkJoin } from 'rxjs';

export interface WorkflowItem {
  id: string;
  flowName: string;
  version: number;
  status: string;
  source: string;
  createdAt: string;
  updatedAt: string;
}

export interface StatusColumn {
  id: string;
  name: string;
  displayOrder: number;
  cases: CaseCard[];
}

export interface CaseCard {
  caseId: string;
  workflowName: string;
  status: string;
  statusId: string;
  assignedTo: string;
  teamId: string;
  createdAt: string;
  totalSop: number;
  completedSop: number;
  progress: number;
  waid: string;
}

const CHANNEL_ICONS: Record<string, string> = {
  WHATSAPP:   'pi-whatsapp',
  SMS:        'pi-mobile',
  VOICE:      'pi-phone',
  CHATBOT:    'pi-comment',
  WEB_WIDGET: 'pi-globe',
};

const CHANNEL_LABELS: Record<string, string> = {
  WHATSAPP:   'WhatsApp',
  SMS:        'SMS',
  VOICE:      'Voice',
  CHATBOT:    'Chatbot',
  WEB_WIDGET: 'Web Widget',
};

@Component({
  selector: 'app-manager-view',
  templateUrl: './manager-view.html',
  styleUrl: './manager-view.scss',
  standalone: true,
  imports: [CommonModule, AppDatePipe, FormsModule, ProgressSpinnerModule, SelectModule, RouterModule]
})
export class ManagerView implements OnInit {
  workflows: WorkflowItem[] = [];
  selectedWorkflow: WorkflowItem | null = null;
  loadingWorkflows = false;
  workflowError = false;

  columns: StatusColumn[] = [];
  loadingBoard = false;
  boardError = false;

  constructor(
    private workflowService: WorkflowService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadWorkflows();
  }

  loadWorkflows(): void {
    this.loadingWorkflows = true;
    this.workflowError = false;

    this.workflowService.getCompanyWorkflows().subscribe({
      next: (res: any) => {
        this.loadingWorkflows = false;
        const raw: WorkflowItem[] = res?.data ?? res ?? [];
        this.workflows = raw
          .filter(w => w.status === 'ACTIVE')
          .sort((a, b) => a.flowName.localeCompare(b.flowName));
      },
      error: () => {
        this.loadingWorkflows = false;
        this.workflowError = true;
      }
    });
  }

  onWorkflowSelect(workflow: WorkflowItem | null): void {
    if (!workflow) {
      this.columns = [];
      return;
    }
    this.loadBoard(workflow.id);
  }

  private loadBoard(workflowId: string): void {
    this.loadingBoard = true;
    this.boardError = false;
    this.columns = [];

    forkJoin({
      statuses: this.workflowService.getWorkflowStatuses(workflowId),
      cases: this.workflowService.getWorkflowCasesByWorkflow(workflowId),
    }).subscribe({
      next: ({ statuses, cases }) => {
        const rawStatuses: any[] = statuses?.data ?? statuses ?? [];
        const rawCases: CaseCard[] = cases?.data ?? cases ?? [];

        // Build columns — new API uses statusId / statusName / order
        this.columns = rawStatuses
          .sort((a, b) => (a.order ?? a.displayOrder ?? 0) - (b.order ?? b.displayOrder ?? 0))
          .map(s => ({
            id:           s.statusId ?? s.id,
            name:         s.statusName ?? s.name,
            displayOrder: s.order ?? s.displayOrder,
            cases: rawCases
              .filter((c: any) => c.statusId === (s.statusId ?? s.id))
              .map((c: any) => ({
                caseId:        c.caseId ?? '',
                workflowName:  c.workflowName ?? '',
                status:        c.status ?? '',
                statusId:      c.statusId ?? '',
                assignedTo:    c.assignedTo ?? '',
                teamId:        c.teamId ?? '',
                createdAt:     c.createdAt ?? '',
                totalSop:      c.totalSop ?? 0,
                completedSop:  c.completedSop ?? 0,
                progress:      c.progress ?? 0,
                waid:          c.waid ?? c.waId ?? '',
              })),
          }));

        this.loadingBoard = false;
      },
      error: () => {
        this.loadingBoard = false;
        this.boardError = true;
      }
    });
  }

  openCase(card: CaseCard): void {
    if (!card.waid) return;
    this.router.navigate(['/conversations'], {
      queryParams: { waId: card.waid, caseId: card.caseId, statusId: card.statusId, fromManager: 'true' }
    });
  }

  channelIcon(channel: string): string {
    return CHANNEL_ICONS[channel] ?? 'pi-circle';
  }

  channelLabel(channel: string): string {
    return CHANNEL_LABELS[channel] ?? (channel || 'Unknown');
  }

  progressPercent(card: CaseCard): number {
    if (!card.totalSop) return 0;
    return Math.round((card.completedSop / card.totalSop) * 100);
  }

  formatFlowName(name: string): string {
    return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }

  get totalCases(): number {
    return this.columns.reduce((sum, col) => sum + col.cases.length, 0);
  }
}
